import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageSquare, Plus, Megaphone, Eye, EyeOff, Siren } from 'lucide-react';
import { UrgentListDialog, useUrgentList } from '../components/chat/UrgentListDialog';
import { BroadcastDialog } from '../components/chat/BroadcastDialog';
import { ChatConvItem } from '../components/chat/ChatConvItem';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../components/chat/chatShared';
import { ChatThread } from '../components/chat/ChatThread';
import { NewChatDialog } from '../components/chat/NewChatDialog';

export default function ChatPage() {
  const { user } = useAuth();
  const location = useLocation();
  const qs = new URLSearchParams(location.search);
  const navigate = useNavigate();
  const [convs, setConvs] = useState(null);
  const activeId = qs.get('c');
  const setActiveId = (id) => {
    if (id === activeId) return;
    if (!id) { if (location.state?.fromList) navigate(-1); else navigate('/chat', { replace: true }); return; }
    navigate(`/chat?c=${encodeURIComponent(id)}`, { replace: !!activeId, state: { fromList: activeId ? !!location.state?.fromList : true } });
  };
  const [contacts, setContacts] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const canOversee = ['super_admin', 'gestor'].includes(user?.role);
  const urgentList = useUrgentList(canOversee || user?.role === 'admin');
  const [urgOpen, setUrgOpen] = useState(false);
  const openUrgent = (m) => { if (!m.is_member && !showAll) setShowAll(true); setActiveId(m.conversation_id); setTimeout(() => document.getElementById(`chatmsg-${m.id}`)?.scrollIntoView({ block: 'center' }), 1500); };
  const [showAll, setShowAllRaw] = useState(() => localStorage.getItem('chat_show_others') === '1');
  const setShowAll = (v) => { setShowAllRaw(v); localStorage.setItem('chat_show_others', v ? '1' : '0'); };
  const [pendingRef, setPendingRef] = useState(() => (qs.get('ref_type') ? { type: qs.get('ref_type'), id: qs.get('ref_id'), label: qs.get('ref_label'), url: qs.get('ref_url') } : null));

  const loadConvs = useCallback(() => {
    axios.get(`${API_URL}/chat/conversations`, { params: showAll ? { all: true } : {} }).then((r) => setConvs(r.data)).catch(() => {});
  }, [showAll]);
  useEffect(() => { loadConvs(); const t = setInterval(loadConvs, 10000); return () => clearInterval(t); }, [loadConvs]);
  useEffect(() => { axios.get(`${API_URL}/chat/contacts`).then((r) => setContacts(r.data)).catch(() => {}); }, []);

  const active = (convs || []).find((c) => c.id === activeId);
  const canGroup = ['super_admin', 'gestor', 'chefe_equipa'].includes(user?.role);
  const canBroadcast = ['super_admin', 'admin', 'gestor'].includes(user?.role);
  const [bcOpen, setBcOpen] = useState(false);

  return (
    <Layout>
      <div className="h-[calc(100dvh-150px)] md:h-[calc(100vh-120px)] min-h-[420px] rounded-2xl border border-slate-200 bg-white overflow-hidden flex" data-testid="chat-page">
        <aside className={`${active ? 'hidden md:flex' : 'flex'} w-full md:w-80 flex-col border-r border-slate-100`}>
          <div className="p-3 border-b border-slate-100 flex items-center gap-2">
            <h1 className="text-lg font-bold font-[Outfit] text-[#2D3E6F] flex items-center gap-2 flex-1"><MessageSquare className="w-5 h-5" />Chat</h1>
            {(canOversee || user?.role === 'admin') && (
              <button type="button" onClick={() => setUrgOpen(true)} title="Pedidos urgentes por resolver" className={`relative p-2 rounded-full ${urgentList.length ? 'bg-rose-600 text-white animate-pulse' : 'bg-slate-100 text-slate-500'}`} data-testid="chat-urgent-list-btn">
                <Siren className="w-4 h-4" />
                {urgentList.length > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-white text-rose-600 text-[10px] font-bold flex items-center justify-center border border-rose-600" data-testid="chat-urgent-list-count">{urgentList.length}</span>}
              </button>
            )}
            {canBroadcast && (
              <button type="button" onClick={() => setBcOpen(true)} title="Aviso para todos os comerciais" className="p-2 rounded-full bg-amber-500 text-white hover:bg-amber-600" data-testid="chat-broadcast-btn"><Megaphone className="w-4 h-4" /></button>
            )}
            <button type="button" onClick={() => setNewOpen(true)} className="p-2 rounded-full bg-[#2D3E6F] text-white hover:bg-[#24335c]" data-testid="chat-new-btn"><Plus className="w-4 h-4" /></button>
          </div>
          {canOversee && (
            <button type="button" onClick={() => setShowAll(!showAll)} data-testid="chat-show-all"
              className={`flex items-center gap-2 px-3 py-2 text-xs border-b border-slate-100 transition-colors ${showAll ? 'bg-amber-50 text-amber-800' : 'text-slate-500 hover:bg-slate-50'}`}>
              {showAll ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              <span className="flex-1 text-left">{showAll ? 'A ver conversas de outras pessoas' : 'Conversas de outras pessoas escondidas'}</span>
              <span className={`w-8 h-4 rounded-full relative transition-colors ${showAll ? 'bg-amber-500' : 'bg-slate-300'}`}>
                <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${showAll ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </span>
            </button>
          )}
          {pendingRef && !active && <p className="text-xs bg-sky-50 text-sky-800 px-3 py-2" data-testid="chat-choose-conv-hint">Escolha a conversa para enviar: {pendingRef.label}</p>}
          <div className="flex-1 overflow-y-auto divide-y divide-slate-50">
            {(convs || []).map((c) => <ChatConvItem key={c.id} c={c} me={user} active={c.id === activeId} onSelect={setActiveId} />)}
            {convs === null && <p className="p-4 text-sm text-slate-400" data-testid="chat-convs-loading">A carregar conversas…</p>}
            {convs?.length === 0 && <p className="p-4 text-sm text-slate-400">Sem conversas. Carregue em + para começar.</p>}
          </div>
        </aside>
        <section className={`${active ? 'flex' : 'hidden md:flex'} flex-1 min-w-0 flex-col`}>
          {active ? (
            <ChatThread key={active.id} conv={active} me={user} pendingRef={pendingRef} clearRef={() => setPendingRef(null)} onBack={() => setActiveId(null)} onActivity={loadConvs} onOpenConv={(id) => { loadConvs(); setActiveId(id); }} />
          ) : <div className="m-auto text-sm text-slate-400 text-center px-4">{activeId && convs === null ? 'A abrir conversa…'
            : activeId && canOversee && !showAll ? <button type="button" onClick={() => setShowAll(true)} className="underline text-amber-700" data-testid="chat-foreign-hint">Esta conversa não é sua — ver conversas de outras pessoas</button>
            : 'Escolha uma conversa'}</div>}
        </section>
      </div>
      <NewChatDialog open={newOpen} onClose={() => setNewOpen(false)} contacts={contacts} canGroup={canGroup}
        onCreated={(id) => { loadConvs(); setActiveId(id); }} />
      <BroadcastDialog open={bcOpen} onClose={() => setBcOpen(false)} onSent={(id) => { loadConvs(); setActiveId(id); }} />
      <UrgentListDialog open={urgOpen} onClose={() => setUrgOpen(false)} list={urgentList} onOpenConv={openUrgent} />
    </Layout>
  );
}
